import { useToast } from "@/components";
import { PulseLoader } from "@/components/pulse_loader";
import { StripeProviderV2 } from "@/core/stripe";
import { useMediaQuery } from "@/hooks";
import {
  useStripe,
  useElements,
  CardNumberElement,
} from "@stripe/react-stripe-js";
import classNames from "classnames";
import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { useDetachPaymentMethod } from "../hooks/use_detach_payment_method";
import { useMembershipManagementAnalytics } from "../hooks/use_membership_management_analytics";
import { useSubscriptionDetailsQuery } from "../hooks/use_subscription_details_query";
import { PortalButton } from "./portal_button";
import { PortalPaymentForm } from "./portal_payment_form";
import { usePartnerPaymentConfigsQuery } from "@/offering/api";

interface PortalChangePaymentMethodModalProps {
  show: boolean;
  onHide: () => void;
  onSucceed: () => void;
  clientSecret: string;
  partnerSlug: string;
  partnerId: string;
  subscriptionId: string;
}

export function PortalChangePaymentMethodModal({
  show,
  onHide,
  onSucceed,
  clientSecret,
  partnerId,
  partnerSlug,
  subscriptionId,
}: PortalChangePaymentMethodModalProps) {
  const { data: paymentConfig } = usePartnerPaymentConfigsQuery(partnerId);

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      className="header-center"
      fullscreen="sm-down"
    >
      <Modal.Header closeButton>
        <Modal.Title as="b">Change Payment Method</Modal.Title>
      </Modal.Header>
      <StripeProviderV2
        partnerSlug={partnerSlug}
        clientSecret={clientSecret}
        stripePublishableKey={paymentConfig?.stripePublishableKey}
      >
        <PortalChangePaymentMethodModalBody
          clientSecret={clientSecret}
          onSucceed={onSucceed}
          partnerId={partnerId}
          subscriptionId={subscriptionId}
        />
      </StripeProviderV2>
    </Modal>
  );
}

interface PortalChangePaymentMethodModalBodyProps {
  onSucceed: () => void;
  clientSecret: string;
  partnerId: string;
  subscriptionId: string;
}

function PortalChangePaymentMethodModalBody({
  onSucceed,
  clientSecret,
  partnerId,
  subscriptionId,
}: PortalChangePaymentMethodModalBodyProps) {
  const stripe = useStripe();
  const elements = useElements();
  const toast = useToast();
  const { device } = useMediaQuery();

  const analyticEvent = useMembershipManagementAnalytics();
  const { data: subscriptionDetailsData } = useSubscriptionDetailsQuery(
    partnerId,
    subscriptionId
  );
  const detachPaymentMethod = useDetachPaymentMethod();

  const [isUpdatingPayment, setIsUpdatingPayment] = useState(false);
  const [nameOnCard, setNameOnCard] = useState("");
  const [nameOnCardError, setNameOnCardError] = useState<string>();

  const handleSavePaymentMethod = async () => {
    try {
      if (!nameOnCard?.trim()) {
        setNameOnCardError("Please enter name on card");
        return;
      } else {
        setNameOnCardError(undefined);
      }

      if (!elements || !stripe) {
        throw new Error("Card processor not loaded");
      }

      const card = elements.getElement(CardNumberElement);

      if (!card) {
        throw new Error("Card processor not loaded");
      }

      setIsUpdatingPayment(true);
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: card,
          billing_details: {
            name: nameOnCard,
          },
        },
      });

      if (
        result.setupIntent?.status === "succeeded" &&
        subscriptionDetailsData?.defaultPaymentMethod?.id
      ) {
        await detachPaymentMethod(
          partnerId,
          subscriptionId,
          subscriptionDetailsData.defaultPaymentMethod.id
        );
        toast.success({ message: "Payment method changed successfully!" });
        onSucceed();
        const isPaymentFailed =
          subscriptionDetailsData?.paymentStatus === "payment_failed";
        analyticEvent(
          isPaymentFailed
            ? "Update Payment Method Save"
            : "Change Payment Method Save"
        );
      } else {
        throw new Error("Fail to update payment method");
      }
    } catch (error: any) {
      toast.error({ message: error.message });
    }
    setIsUpdatingPayment(false);
  };

  return (
    <>
      <Modal.Body>
        <PortalPaymentForm
          nameOnCard={nameOnCard}
          setNameOnCard={setNameOnCard}
          nameOnCardError={nameOnCardError}
        />
      </Modal.Body>
      <Modal.Footer>
        <div className={classNames(device === "mobile" && "w-100")}>
          {isUpdatingPayment ? (
            <div className="text-center">
              <PulseLoader />
            </div>
          ) : (
            <PortalButton onClick={handleSavePaymentMethod}>
              Save payment method
            </PortalButton>
          )}
        </div>
      </Modal.Footer>
    </>
  );
}
